import React, { useState } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faEdit,
  faTrash,
  faReceipt,
  faMoneyBill,
  faFilter,
  faChartLine,
  faEye,
  faSpinner,
  faCheckCircle,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line no-unused-vars
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Accept: "text/plain",
        },
      }
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function ItemizedSalesRegister() {
  const { stateNavigator } = useNavigationEvent();

  const userId = ls.get("currentuserid");

  const [filterPaymentType, setFilterPaymentType] = useState("all");
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const paymentOptions = [
    { value: "cash", label: "Cash", icon: faMoneyBill },
    { value: "credit_card", label: "Credit Card" },
    { value: "debit_card", label: "Debit Card" },
    { value: "mobile_money", label: "Mobile Money" },
  ];

  // SWR Data fetching for Sales
  const { data: salessales, mutate: mutatesales } = useSWR(
    `${process.env.REACT_APP_SALES_ORDER_API}?include=field_cashier,field_customer&sort=-created&page[limit]=50`,
    fetcher
  );
  console.log("Sales Data:", salessales);

  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_customer_email,field_phone_number,field_customer_address,drupal_internal__nid`,
    fetcher
  );
  console.log("Customers Data NOW:", customers);

  const { data: users } = useSWR(
    `${process.env.REACT_APP_USER_API}?&filter[drupal_internal__uid]=${userId}`,
    fetcher
  );

  const filteredsales =
    salessales?.data?.filter((sale) => {
      if (filterPaymentType === "all") return true;
      return sale.attributes.field_payment_type === filterPaymentType;
    }) || [];

  const calculateStatistics = () => {
    if (!salessales?.data)
      return { totalRevenue: 0, totalsales: 0, averagesale: 0 };

    const sales = salessales.data;
    const totalRevenue = sales.reduce(
      (sum, sale) => sum + parseFloat(sale.attributes.field_total_amount || 0),
      0
    );
    const totalsales = sales.length;
    const averagesale = totalsales > 0 ? totalRevenue / totalsales : 0;

    return { totalRevenue, totalsales, averagesale };
  };

  const { totalRevenue, totalsales, averagesale } = calculateStatistics();

  const handleDelete = async (salesId) => {
    try {
      const freshToken = await getCsrfToken();

      if (!freshToken) {
        setErrorMessage("Unable to authenticate. Please refresh the page.");
        setModalError(true);
        return;
      }

      if (window.confirm("Are you sure you want to delete this sale?")) {
        const res = await fetch(
          `${process.env.REACT_APP_SALES_ORDER_API}/${salesId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": freshToken,
            },
            credentials: "include",
          }
        );

        if (res.ok) {
          setModalSuccessMsg(true);
          mutatesales();
        } else {
          setErrorMessage("Failed to delete sale");
          setModalError(true);
        }
      }
    } catch (error) {
      setErrorMessage("Network error");
      setModalError(true);
    }
  };

  const getCashierName = (sale) => {
    const cashierId = sale.relationships?.field_cashier?.data?.id;
    if (!cashierId || !users?.data) return "N/A";
    const user = users.data.find((u) => u.id === cashierId);
    return user?.attributes?.name || "N/A";
  };

  const getCustomerName = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.title || "N/A";
  };

  const handleFilter = () => {
    // Implement date range filtering
    mutatesales();
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="sales-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Itemized Sales Register"
            buttonLabel="Sales Register"
            icon={faAdd}
            onClick={() => stateNavigator.navigate("viewsalesregister")}
          />

          {/* FILTER BAR */}
          <div style={styles.filterBar}>
            <select
              style={styles.filterSelect}
              value={filterPaymentType}
              onChange={(e) => setFilterPaymentType(e.target.value)}
            >
              <option value="all">All Payment Types</option>
              {paymentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              type="date"
              style={styles.filterInput}
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
            />
            <input
              type="date"
              style={styles.filterInput}
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
            />
            <button style={styles.filterButton} onClick={handleFilter}>
              <FontAwesomeIcon icon={faFilter} /> Filter
            </button>
          </div>

          {/* STATISTICS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faReceipt} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{totalsales}</h3>
                <p style={styles.statLabel}>Total Sales</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faMoneyBill} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{totalRevenue.toFixed(2)}</h3>
                <p style={styles.statLabel}>Total Sales Revenue</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faChartLine} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{averagesale.toFixed(2)}</h3>
                <p style={styles.statLabel}>Average Sale</p>
              </div>
            </div>
          </div>

          {/* SALE LIST TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Cashier</th>
                    <th>Payment Type</th>
                    <th>Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!salessales ? (
                    <tr>
                      <td
                        colSpan="8"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Loading Sales...
                      </td>
                    </tr>
                  ) : filteredsales.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        No sales found
                      </td>
                    </tr>
                  ) : (
                    filteredsales.map((sale) => {
                      return (
                        <tr key={sale.id}>
                          <td>{sale.attributes.field_order_number}</td>
                          <td>
                            {new Date(
                              sale.attributes.created
                            ).toLocaleDateString()}
                          </td>
                          <td>{getCustomerName(sale)}</td>
                          <td>{getCashierName(sale)}</td>
                          <td>
                            <span style={styles.paymentBadge}>
                              {sale.attributes.field_payment_type}
                            </span>
                          </td>
                          <td>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_amount
                            ).toFixed(2)}
                          </td>
                          <td style={styles.actionBTN}>
                            <button
                              style={styles.viewButton}
                              onClick={() =>
                                stateNavigator.navigate(
                                  "viewsalesinvoicedetails",
                                  { salesId: sale.id }
                                )
                              }
                              title="View Invoice"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              style={styles.editButton}
                              onClick={() =>
                                stateNavigator.navigate("editsalesorder", {
                                  salesId: sale.id,
                                  salesOrderNumber:
                                    sale.attributes.field_order_number,
                                  salePaymentType:
                                    sale.attributes.field_payment_type,
                                  salesCustomerName: getCustomerName(sale),
                                })
                              }
                              title="Edit Sale"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              style={styles.deleteButton}
                              onClick={() => handleDelete(sale.id)}
                              title="Delete Sale"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message="sale deleted successfully!"
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faFilter}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faFilter}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px",
  },
  th: {
    background: "#f8f9fa",
    padding: "14px",
    textAlign: "left",
    borderBottom: "1px solid #dee2e6",
    fontWeight: "600",
  },
  td: {
    padding: "14px",
    borderBottom: "1px solid #dee2e6",
  },
  iconButton: {
    color: "white",
    border: "none",
    borderRadius: "4px",
    // padding: "6px 10px",
    margin: "0 2px",
    cursor: "pointer",
  },
  paymentBadge: {
    background: "#e3f2fd",
    color: "#1976d2",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  statIcon: {
    fontSize: "20px",
    color: "#007bff",
  },
  statValue: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "14px",
  },
  filterBar: {
    display: "flex",
    gap: "15px",
    marginBottom: "20px",
    alignItems: "center",
  },
  filterSelect: {
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    background: "white",
    minWidth: "200px",
  },
  filterInput: {
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    minWidth: "150px",
  },
  filterButton: {
    background: "#6c757d",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  actionBTN: {
    display: "flex",
    gap: "8px",
  },
  viewButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#bbdefb",
    },
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#ffcdd2",
    },
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#c8e6c9",
    },
  },
};
